import React, { useState, useEffect } from 'react';

const SystemManagerAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
  const authHeadersForForm = { 'Authorization': `Bearer ${adminToken}` };

  // State vars
  const [protectedFeatures, setProtectedFeatures] = useState([]);
  const [newProtectedFeature, setNewProtectedFeature] = useState({
    name: '',
    location: '',
    description: '',
    notes_for_ai: '',
    status: 'working'
  });
  const [editingProtectedFeature, setEditingProtectedFeature] = useState(null);
  const [featuresSummary, setFeaturesSummary] = useState(null);
  const [selectedFeatureInfo, setSelectedFeatureInfo] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isCheckingChanges, setIsCheckingChanges] = useState(false);
  const [featuresFilter, setFeaturesFilter] = useState('all');
  const [systemManagerSubTab, setSystemManagerSubTab] = useState('route-migration');
  const [dbConnector, setDbConnector] = useState({
    migrationStats: null,
    migrationOldDomain: '',
    migrationNewDomain: '',
    migrationScanning: false,
    currentMigrationSession: null,
    migrationScanResults: null,
    migrationSessions: [],
    selectedMigrationSession: null,
    sessionRoutes: [],
    isLoading: false
  });

  // Load protected features data
  const loadProtectedFeaturesData = async () => {
    try {
      const protectedRes = await fetch(`${API}/admin/protected-features`, {
        headers: authHeaders
      });
      if (protectedRes.ok) {
        const protectedData = await protectedRes.json();
        const validFeatures = (Array.isArray(protectedData) ? protectedData : []).filter(f => f && f.id);
        setProtectedFeatures(validFeatures);
      } else {
        setProtectedFeatures([]);
      }
      const summaryRes = await fetch(`${API}/admin/protected-features/summary`, {
        headers: authHeaders
      });
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setFeaturesSummary(summaryData);
      }
    } catch (error) {
      console.error("Error loading protected features:", error);
    }
  };

  // Load data when sub-tabs change
  useEffect(() => {
    if (systemManagerSubTab === 'protected-features') {
      loadProtectedFeaturesData();
    }
  }, [systemManagerSubTab]);

  return (
                <div className="space-y-6">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    ⚙️ System Manager
                  </h3>

                  {/* Sub-tab navigation */}
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'route-migration', label: 'Route Migration', icon: '🔀' },
                      { id: 'protected-features', label: 'Protected Features', icon: '🔒' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setSystemManagerSubTab(tab.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          systemManagerSubTab === tab.id
                            ? 'bg-blue-600 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>


                  {systemManagerSubTab === 'route-migration' && (
                <div className="space-y-6">
                  {/* Header with Explanation */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode
                      ? 'border-blue-500 bg-gradient-to-br from-blue-900/30 to-cyan-900/20'
                      : 'border-blue-400 bg-gradient-to-br from-blue-50 to-cyan-50'
                  }`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-4xl">🔀</span>
                      <div>
                        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          Route Migration Manager
                        </h2>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Automatically find and replace URLs when moving between servers
                        </p>
                      </div>
                    </div>

                    {/* Quick Guide Box */}
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-300'}`}>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        <span>📋</span> Quick Guide - What To Do:
                      </h4>
                      <div className={`text-sm space-y-2 ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
                        <div className={`p-2 rounded ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                          <strong>On VPS (Production):</strong>
                          <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                            <li>Click <strong>"Run Auto-Detect Now"</strong> below</li>
                            <li>It finds any localhost URLs still in your code</li>
                            <li>Click <strong>"Fix This"</strong> on each domain found</li>
                            <li>Scan and <strong>"Fix All"</strong> to apply</li>
                          </ol>
                        </div>
                        <div className={`p-2 rounded ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-100'}`}>
                          <strong>On Localhost (Development):</strong>
                          <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                            <li>Click <strong>"Run Auto-Detect Now"</strong></li>
                            <li>It finds any VPS/production URLs in your code</li>
                            <li>Click <strong>"Fix This"</strong> to replace with localhost</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    {/* How It Works Explanation */}
                    <div className={`p-4 rounded-lg mt-4 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                      <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        How It Works:
                      </h4>
                      <ol className={`text-sm space-y-1 list-decimal list-inside ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                        <li><strong>Step 1:</strong> Enter the OLD URL you want to replace (e.g., localhost:3000)</li>
                        <li><strong>Step 2:</strong> Enter the NEW URL to replace it with (e.g., your-domain.com)</li>
                        <li><strong>Step 3:</strong> Click "Scan" to find all occurrences in files and database</li>
                        <li><strong>Step 4:</strong> Preview the changes, then click "Fix All" to apply</li>
                      </ol>
                      <p className={`mt-2 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`}>
                        Use this when moving from localhost to VPS, or when changing domains.
                      </p>
                    </div>
                  </div>

                  {/* AUTO-DETECT SECTION */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-orange-900/30 to-amber-900/20 border-orange-500'
                      : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-400'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🔎</span>
                        <div>
                          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            Auto-Detect Mismatched URLs
                          </h3>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Automatically scan for URLs that don't belong to your current environment
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={dbConnector.autoDetectEnabled || false}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setDbConnector(prev => ({ ...prev, autoDetectEnabled: enabled }));
                              if (enabled) {
                                // Run initial scan
                                runAutoDetectScan();
                              }
                            }}
                            className="w-5 h-5 rounded"
                          />
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                            Enable Auto-Detect
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Current Environment Info */}
                    <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Current Environment:
                      </p>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          window.location.hostname === 'localhost'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {window.location.hostname === 'localhost' ? '🏠 Localhost' : '🌐 VPS/Production'}
                        </span>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {window.location.origin}
                        </span>
                      </div>
                    </div>

                    {/* Domains to Watch For */}
                    <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Looking for these mismatched domains:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(window.location.hostname === 'localhost' ? [
                          'emergent.com', process.env.REACT_APP_VPS_IP || 'VPS_IP', 'darijapro.com'
                        ] : [
                          'localhost', '127.0.0.1', 'emergent.com'
                        ]).map((domain, idx) => (
                          <span key={idx} className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                            {domain}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Scan Button */}
                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={async () => {
                          setDbConnector(prev => ({ ...prev, autoDetectScanning: true, autoDetectResults: null, autoDetectStopped: false }));
                          try {
                            // Determine which domains to look for based on current environment
                            const domainsToFind = window.location.hostname === 'localhost'
                              ? ['emergent.com', process.env.REACT_APP_VPS_IP || 'VPS_IP']
                              : ['localhost', '127.0.0.1'];

                            const allResults = [];
                            let stopped = false;

                            for (const domain of domainsToFind) {
                              // Check if user stopped the scan
                              if (dbConnector.autoDetectStopped) {
                                stopped = true;
                                break;
                              }

                              try {
                                console.log(`[Auto-Detect] Scanning for: ${domain}`);
                                const res = await fetch(`${API}/migration/smart-scan`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${adminToken}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    old_domain: domain,
                                    new_domain: 'DETECT_ONLY'
                                  })
                                });

                                if (!res.ok) {
                                  console.error(`[Auto-Detect] HTTP error for ${domain}: ${res.status}`);
                                  continue;
                                }

                                const data = await res.json();
                                console.log(`[Auto-Detect] Result for ${domain}:`, data);

                                if (data.success && data.result?.routes?.length > 0) {
                                  allResults.push({
                                    domain,
                                    count: data.result.routes.length,
                                    routes: data.result.routes,
                                    fileCount: data.result.file_routes || 0,
                                    dbCount: data.result.database_routes || 0
                                  });
                                }
                              } catch (domainErr) {
                                console.error(`[Auto-Detect] Error scanning ${domain}:`, domainErr);
                              }
                            }

                            setDbConnector(prev => ({
                              ...prev,
                              autoDetectScanning: false,
                              autoDetectResults: allResults,
                              autoDetectLastScan: new Date().toISOString(),
                              autoDetectStopped: stopped
                            }));

                            if (stopped) {
                              showNotification('Scan stopped by user', 'info');
                            } else if (allResults.length === 0) {
                              showNotification('No mismatched URLs found! Your codebase is clean.', 'success');
                            } else {
                              const totalIssues = allResults.reduce((sum, r) => sum + r.count, 0);
                              showNotification(`Found ${totalIssues} mismatched URLs from ${allResults.length} domain(s)`, 'warning');
                            }
                          } catch (err) {
                            console.error('[Auto-Detect] Scan failed:', err);
                            setDbConnector(prev => ({ ...prev, autoDetectScanning: false }));
                            showNotification('Auto-detect scan failed: ' + err.message, 'error');
                          }
                        }}
                        disabled={dbConnector.autoDetectScanning}
                        className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
                          dbConnector.autoDetectScanning
                            ? 'bg-gray-400 cursor-not-allowed text-white'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        {dbConnector.autoDetectScanning ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Scanning...
                          </>
                        ) : (
                          <>🔎 Run Auto-Detect Now</>
                        )}
                      </button>

                      {dbConnector.autoDetectScanning && (
                        <button
                          onClick={() => {
                            setDbConnector(prev => ({
                              ...prev,
                              autoDetectScanning: false,
                              autoDetectStopped: true
                            }));
                            showNotification('Auto-detect scan stopped', 'info');
                          }}
                          className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white"
                        >
                          ⏹️ Stop
                        </button>
                      )}

                      {dbConnector.autoDetectLastScan && (
                        <span className={`text-xs self-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Last scan: {new Date(dbConnector.autoDetectLastScan).toLocaleTimeString()}
                          {dbConnector.autoDetectStopped && ' (stopped)'}
                        </span>
                      )}
                    </div>

                    {/* Note about page refresh */}
                    <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Note: Scan stops if you refresh the page. Results are not saved between sessions.
                    </p>

                    {/* Auto-Detect Results */}
                    {dbConnector.autoDetectResults && dbConnector.autoDetectResults.length > 0 && (
                      <div className={`border-2 rounded-lg overflow-hidden ${isDarkMode ? 'border-red-700' : 'border-red-400'}`}>
                        <div className={`p-3 ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">⚠️</span>
                            <h4 className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                              Mismatched URLs Detected
                            </h4>
                            <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold ${isDarkMode ? 'bg-red-800 text-red-200' : 'bg-red-200 text-red-800'}`}>
                              {dbConnector.autoDetectResults.reduce((sum, r) => sum + r.count, 0)} issues
                            </span>
                          </div>
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                          {dbConnector.autoDetectResults.map((result, idx) => (
                            <div key={idx} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              {/* Domain Header */}
                              <div
                                className={`p-3 cursor-pointer flex items-center justify-between ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}
                                onClick={() => {
                                  setDbConnector(prev => ({
                                    ...prev,
                                    expandedAutoDetectDomain: prev.expandedAutoDetectDomain === result.domain ? null : result.domain
                                  }));
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`transform transition-transform ${dbConnector.expandedAutoDetectDomain === result.domain ? 'rotate-90' : ''}`}>
                                    ▶
                                  </span>
                                  <span className={`font-mono font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                    {result.domain}
                                  </span>
                                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ({result.fileCount} files, {result.dbCount} database)
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                    {result.count} URLs
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Pre-fill the manual scan with this domain
                                      setDbConnector(prev => ({
                                        ...prev,
                                        oldBaseUrl: result.domain.includes('://') ? result.domain : `http://${result.domain}`,
                                        newBaseUrl: window.location.origin
                                      }));
                                      showNotification(`Set "${result.domain}" as old URL. Configure new URL and scan to fix.`, 'info');
                                    }}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                  >
                                    Fix This
                                  </button>
                                </div>
                              </div>

                              {/* Expanded File List */}
                              {dbConnector.expandedAutoDetectDomain === result.domain && (
                                <div className={`p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {result.routes.slice(0, 20).map((route, rIdx) => (
                                      <div key={rIdx} className={`p-2 rounded text-sm ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                        <div className="flex items-start gap-2">
                                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                                            route.location_type === 'file'
                                              ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                                              : isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                                          }`}>
                                            {route.location_type}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className={`font-mono text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                              {route.location}
                                            </div>
                                            <div className={`font-mono text-xs truncate ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                              {route.old_route}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {result.routes.length > 20 && (
                                      <div className={`text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        ... and {result.routes.length - 20} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Clean Status */}
                    {dbConnector.autoDetectResults && dbConnector.autoDetectResults.length === 0 && (
                      <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-400'}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">✅</span>
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                              All Clear! No mismatched URLs found.
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              Your codebase is clean for the current environment.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* STEP 1 & 2: URL CONFIGURATION */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode ? 'bg-gradient-to-br from-green-900/30 to-teal-900/20 border-green-500' : 'bg-gradient-to-br from-green-50 to-teal-50 border-green-400'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">1️⃣</span>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Configure URLs
                      </h3>
                    </div>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Enter the URL you want to find (old) and what to replace it with (new).
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Find this URL (OLD)
                        </label>
                        <input
                          type="text"
                          value={dbConnector.oldBaseUrl || ''}
                          onChange={(e) => setDbConnector(prev => ({ ...prev, oldBaseUrl: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          placeholder="e.g., http://localhost:3000"
                        />
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          The URL currently in your code/database
                        </p>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Replace with this URL (NEW)
                        </label>
                        <input
                          type="text"
                          value={dbConnector.newBaseUrl || ''}
                          onChange={(e) => setDbConnector(prev => ({ ...prev, newBaseUrl: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          placeholder="e.g., https://your-domain.com"
                        />
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          The URL you want to use instead
                        </p>
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Quick Presets:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setDbConnector(prev => ({
                            ...prev,
                            oldBaseUrl: 'http://localhost:3000',
                            newBaseUrl: process.env.REACT_APP_VPS_URL || 'https://VPS_IP'
                          }))}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium ${isDarkMode ? 'bg-green-900/50 text-green-300 hover:bg-green-900/70' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        >
                          Localhost → VPS
                        </button>
                        <button
                          onClick={() => setDbConnector(prev => ({
                            ...prev,
                            oldBaseUrl: process.env.REACT_APP_VPS_URL || 'https://VPS_IP',
                            newBaseUrl: 'http://localhost:3000'
                          }))}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium ${isDarkMode ? 'bg-yellow-900/50 text-yellow-300 hover:bg-yellow-900/70' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}
                        >
                          VPS → Localhost
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* STEP 2: SCAN & PREVIEW */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/20 border-blue-500' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-400'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">2️⃣</span>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Scan & Preview
                      </h3>
                    </div>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Click "Scan" to search all files and database for the old URL. Review the results before applying.
                    </p>

                    <div className="flex flex-wrap gap-3 mb-4">
                      <button
                        onClick={async () => {
                          if (!dbConnector.oldBaseUrl) {
                            showNotification('Please enter the old base URL first', 'error');
                            return;
                          }
                          setDbConnector(prev => ({ ...prev, isScanning: true, scanProgress: 'Starting scan...' }));
                          try {
                            const res = await fetch(`${API}/migration/smart-scan`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${adminToken}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                new_domain: dbConnector.newBaseUrl || 'PLACEHOLDER',
                                old_domain: dbConnector.oldBaseUrl
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              if (data.result?.total_routes === 0) {
                                showNotification('No matching URLs found for the specified domain', 'info');
                                setDbConnector(prev => ({
                                  ...prev,
                                  isScanning: false,
                                  scanProgress: '',
                                  scanResults: null,
                                  noResultsFound: true
                                }));
                              } else {
                                setDbConnector(prev => ({
                                  ...prev,
                                  isScanning: false,
                                  scanProgress: '',
                                  scanResults: data.result,
                                  noResultsFound: false
                                }));
                                showNotification(`Found ${data.result?.total_routes || 0} URLs to migrate!`, 'success');
                              }
                            } else {
                              throw new Error(data.detail || 'Scan failed');
                            }
                          } catch (err) {
                            setDbConnector(prev => ({ ...prev, isScanning: false, scanProgress: '' }));
                            showNotification(err.message || 'Scan failed', 'error');
                          }
                        }}
                        disabled={dbConnector.isScanning}
                        className={`px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2 ${
                          dbConnector.isScanning
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                      >
                        {dbConnector.isScanning ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Scanning...
                          </>
                        ) : (
                          <>🔍 Scan for URLs</>
                        )}
                      </button>

                      {dbConnector.isScanning && (
                        <button
                          onClick={() => {
                            setDbConnector(prev => ({ ...prev, isScanning: false, scanProgress: 'Scan cancelled' }));
                            showNotification('Scan cancelled', 'info');
                          }}
                          className="px-6 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 flex items-center gap-2"
                        >
                          ⏹️ Stop Scan
                        </button>
                      )}

                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API}/migration/history`, {
                              headers: { 'Authorization': `Bearer ${adminToken}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setDbConnector(prev => ({ ...prev, showHistoryModal: true, migrationHistory: data.history || [] }));
                            }
                          } catch (err) {
                            showNotification('Failed to load history', 'error');
                          }
                        }}
                        className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        📜 View History
                      </button>
                    </div>

                    {dbConnector.scanProgress && (
                      <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                          {dbConnector.scanProgress}
                        </p>
                      </div>
                    )}

                    {dbConnector.noResultsFound && (
                      <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-400'}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">✅</span>
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                              No URLs found matching "{dbConnector.oldBaseUrl}"
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              Your codebase appears clean of this domain. Try a different search term or check spelling.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Scan Results Preview */}
                    {dbConnector.scanResults && dbConnector.scanResults.total_routes > 0 && (
                      <div className={`mt-4 border rounded-lg ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className={`p-4 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              Preview: {dbConnector.scanResults.total_routes} URLs Found
                            </h4>
                            <div className="flex gap-2">
                              <span className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {dbConnector.scanResults.file_routes || 0} in Files
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                {dbConnector.scanResults.database_routes || 0} in Database
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="max-h-80 overflow-y-auto">
                          {dbConnector.scanResults.routes?.slice(0, 50).map((route, idx) => (
                            <div key={idx} className={`p-3 border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                              <div className="flex items-start gap-3">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  route.location_type === 'file'
                                    ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                                    : isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {route.location_type}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className={`font-mono text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {route.location}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`font-mono text-sm break-all ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                      {route.old_route}
                                    </span>
                                    <span className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>→</span>
                                    <span className={`font-mono text-sm break-all ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                      {route.new_route}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {dbConnector.scanResults.routes?.length > 50 && (
                            <div className={`p-3 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ... and {dbConnector.scanResults.routes.length - 50} more
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className={`p-4 border-t ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex flex-wrap gap-3">
                            <button
                              onClick={() => setDbConnector(prev => ({ ...prev, showFixAllModal: true }))}
                              className="px-6 py-2 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600 flex items-center gap-2"
                            >
                              ✅ Fix All ({dbConnector.scanResults.total_routes})
                            </button>
                            <button
                              onClick={() => setDbConnector(prev => ({ ...prev, scanResults: null }))}
                              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
                            >
                              Clear Results
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* FIX ALL CONFIRMATION MODAL */}
                  {dbConnector.showFixAllModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className={`p-6 rounded-xl max-w-2xl w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">⚠️</span>
                          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            Confirm Fix All Changes
                          </h3>
                        </div>

                        <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-400'}`}>
                          <p className={`font-medium mb-2 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                            This will automatically change:
                          </p>
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                            <li>• <strong>{dbConnector.scanResults?.file_routes || 0}</strong> URLs in source files</li>
                            <li>• <strong>{dbConnector.scanResults?.database_routes || 0}</strong> URLs in database</li>
                          </ul>
                        </div>

                        <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>From:</strong> <span className="font-mono text-red-500">{dbConnector.oldBaseUrl}</span>
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>To:</strong> <span className="font-mono text-green-500">{dbConnector.newBaseUrl}</span>
                          </p>
                        </div>

                        <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                            💡 A backup will be created automatically. You can view and restore from History.
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={async () => {
                              setDbConnector(prev => ({ ...prev, isApplying: true }));
                              try {
                                const res = await fetch(`${API}/migration/apply-all`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${adminToken}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    session_id: dbConnector.scanResults.session_id,
                                    auto_mode: true,
                                    create_backup: true
                                  })
                                });
                                const data = await res.json();
                                if (data.success) {
                                  showNotification(`Successfully fixed ${data.result?.fixed || 0} URLs!`, 'success');
                                  setDbConnector(prev => ({
                                    ...prev,
                                    showFixAllModal: false,
                                    isApplying: false,
                                    scanResults: null,
                                    lastFixResult: data.result
                                  }));
                                } else {
                                  throw new Error(data.detail || 'Fix failed');
                                }
                              } catch (err) {
                                setDbConnector(prev => ({ ...prev, isApplying: false }));
                                showNotification(err.message || 'Fix failed', 'error');
                              }
                            }}
                            disabled={dbConnector.isApplying}
                            className={`flex-1 py-3 rounded-lg text-white font-medium ${
                              dbConnector.isApplying
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-500 hover:bg-green-600'
                            }`}
                          >
                            {dbConnector.isApplying ? '⏳ Applying Changes...' : '✅ Yes, Fix All'}
                          </button>
                          <button
                            onClick={() => setDbConnector(prev => ({ ...prev, showFixAllModal: false }))}
                            className={`px-6 py-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* HISTORY MODAL */}
                  {dbConnector.showHistoryModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className={`p-6 rounded-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">📜</span>
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              Migration History
                            </h3>
                          </div>
                          <button
                            onClick={() => setDbConnector(prev => ({ ...prev, showHistoryModal: false }))}
                            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                          >
                            ✕
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                          {dbConnector.migrationHistory?.length > 0 ? (
                            <div className="space-y-3">
                              {dbConnector.migrationHistory.map((item, idx) => (
                                <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        item.status === 'completed'
                                          ? 'bg-green-500 text-white'
                                          : item.status === 'reverted'
                                            ? 'bg-yellow-500 text-white'
                                            : 'bg-gray-500 text-white'
                                      }`}>
                                        {item.status}
                                      </span>
                                      <span className={`ml-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {new Date(item.created_at).toLocaleString()}
                                      </span>
                                    </div>
                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      {item.changes_count} changes
                                    </span>
                                  </div>
                                  <div className={`font-mono text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <span className="text-red-500">{item.old_domain}</span>
                                    <span className={`mx-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>→</span>
                                    <span className="text-green-500">{item.new_domain}</span>
                                  </div>
                                  {item.status === 'completed' && (
                                    <button
                                      onClick={async () => {
                                        if (!window.confirm('Revert this migration? This will undo all changes.')) return;
                                        try {
                                          const res = await fetch(`${API}/migration/revert/${item.session_id}`, {
                                            method: 'POST',
                                            headers: { 'Authorization': `Bearer ${adminToken}` }
                                          });
                                          if (res.ok) {
                                            showNotification('Migration reverted successfully', 'success');
                                            // Refresh history
                                            const histRes = await fetch(`${API}/migration/history`, {
                                              headers: { 'Authorization': `Bearer ${adminToken}` }
                                            });
                                            if (histRes.ok) {
                                              const histData = await histRes.json();
                                              setDbConnector(prev => ({ ...prev, migrationHistory: histData.history || [] }));
                                            }
                                          }
                                        } catch (err) {
                                          showNotification('Failed to revert', 'error');
                                        }
                                      }}
                                      className="mt-2 px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                    >
                                      ↩️ Revert
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="text-4xl block mb-2">📭</span>
                              No migration history yet
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* LAST FIX RESULT */}
                  {dbConnector.lastFixResult && (
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-400'}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-3xl">✅</span>
                        <div>
                          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                            Migration Completed Successfully!
                          </h3>
                          <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            Fixed {dbConnector.lastFixResult.fixed} URLs
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setDbConnector(prev => ({ ...prev, lastFixResult: null }))}
                        className={`text-sm ${isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'}`}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* STEP 3: MIGRATION SESSIONS / HISTORY */}
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-500' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-400'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">3️⃣</span>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Migration Sessions
                      </h3>
                    </div>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      View past migrations and their status. Each session saves what was changed so you can track history.
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API}/migration/sessions`, {
                              headers: { 'Authorization': `Bearer ${adminToken}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setDbConnector(prev => ({ ...prev, migrationSessions: data.sessions || [] }));
                            }
                          } catch (err) {
                            showNotification('Failed to load sessions', 'error');
                          }
                        }}
                        className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        🔄 Load Sessions
                      </button>
                    </div>

                    {dbConnector.migrationSessions?.length > 0 ? (
                      <div className="space-y-2">
                        {dbConnector.migrationSessions.map((session) => (
                          <div
                            key={session.session_id}
                            className={`p-4 rounded-lg border ${
                              isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {session.old_domain} → {session.new_domain}
                                </div>
                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {new Date(session.created_at).toLocaleString()} |
                                  Routes: {session.stats?.total || 0} |
                                  Fixed: {session.stats?.fixed || 0} |
                                  Pending: {session.stats?.pending || 0}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(`${API}/migration/sessions/${session.session_id}/routes?limit=100`, {
                                        headers: { 'Authorization': `Bearer ${adminToken}` }
                                      });
                                      if (res.ok) {
                                        const data = await res.json();
                                        setDbConnector(prev => ({
                                          ...prev,
                                          selectedMigrationSession: session.session_id,
                                          sessionRoutes: data.routes || []
                                        }));
                                      }
                                    } catch (err) {
                                      showNotification('Failed to load routes', 'error');
                                    }
                                  }}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  View Routes
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Apply all approved fixes? This will update files and database.')) return;
                                    try {
                                      const res = await fetch(`${API}/migration/apply-all`, {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${adminToken}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          session_id: session.session_id,
                                          auto_mode: true
                                        })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        showNotification(`Fixed ${data.result?.fixed || 0} routes!`, 'success');
                                      } else {
                                        showNotification('Apply failed', 'error');
                                      }
                                    } catch (err) {
                                      showNotification('Apply failed', 'error');
                                    }
                                  }}
                                  className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                >
                                  Apply All
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No migration sessions found. Click "Load Sessions" or start a new scan.
                      </p>
                    )}
                  </div>

                  {/* Route Details (when session is selected) */}
                  {dbConnector.selectedMigrationSession && dbConnector.sessionRoutes?.length > 0 && (
                    <div className={`p-6 rounded-xl border ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          Routes in Session
                        </h3>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API}/migration/review-all`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${adminToken}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    session_id: dbConnector.selectedMigrationSession,
                                    action: 'approve'
                                  })
                                });
                                showNotification('All routes approved', 'success');
                              } catch (err) {
                                showNotification('Failed to approve', 'error');
                              }
                            }}
                            className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                          >
                            Approve All
                          </button>
                          <button
                            onClick={() => setDbConnector(prev => ({ ...prev, selectedMigrationSession: null, sessionRoutes: [] }))}
                            className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            Close
                          </button>
                        </div>
                      </div>

                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {dbConnector.sessionRoutes.map((route, idx) => (
                          <div
                            key={route.id || idx}
                            className={`p-3 rounded-lg text-sm ${
                              route.status === 'fixed'
                                ? isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'
                                : route.status === 'skipped'
                                  ? isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                                  : isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className={`font-mono text-xs break-all ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                  - {route.old_route}
                                </div>
                                <div className={`font-mono text-xs break-all ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  + {route.new_route}
                                </div>
                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {route.location_type}: {route.file_path || route.collection}
                                </div>
                              </div>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                route.status === 'fixed' ? 'bg-green-500 text-white' :
                                route.status === 'skipped' ? 'bg-gray-500 text-white' :
                                route.status === 'reviewed' ? 'bg-blue-500 text-white' :
                                'bg-yellow-500 text-white'
                              }`}>
                                {route.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Help Section */}
                  <div className={`p-6 rounded-xl border ${
                    isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      How Route Migration Works
                    </h3>
                    <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p><strong>1. Scan:</strong> Enter your old and new domains, then click "Scan for Routes" to find all occurrences.</p>
                      <p><strong>2. Review:</strong> Check the found routes and approve or skip them as needed.</p>
                      <p><strong>3. Apply:</strong> Click "Apply All" to update all approved routes in files and database.</p>
                      <p className={`mt-3 p-3 rounded ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                        ⚠️ <strong>Tip:</strong> Always create a backup before applying migrations. The system scans both code files and database entries.
                      </p>
                    </div>
                  </div>
                </div>
                  )}

                  {systemManagerSubTab === 'protected-features' && (
                <div className="space-y-6">
                  {/* Header with Summary */}
                  <div className={`p-6 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-red-500 bg-red-900/20' : 'border-red-400 bg-red-50'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          🛡️ Protected Features Registry
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Track all features, detect file changes, and restore files. AI agents read this before making changes.
                        </p>
                      </div>
                      {featuresSummary && (
                        <div className="flex gap-4 text-center">
                          <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                            <div className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{featuresSummary.working}</div>
                            <div className={`text-xs ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Working</div>
                          </div>
                          <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                            <div className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{featuresSummary.needs_testing}</div>
                            <div className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>Needs Test</div>
                          </div>
                          <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                            <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{featuresSummary.broken}</div>
                            <div className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Broken</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={async () => {
                          setIsScanning(true);
                          try {
                            const response = await fetch(`${API}/admin/protected-features/scan`, {
                              method: 'POST',
                              headers: authHeaders
                            });
                            const data = await response.json();
                            showNotification(`Scanned ${data.total_features} features. Added: ${data.added}, Updated: ${data.updated}`, 'success');
                            // Reload features
                            const featuresRes = await fetch(`${API}/admin/protected-features`, { headers: authHeaders });
                            const features = await featuresRes.json();
                            setProtectedFeatures((features || []).filter(f => f && f.id));
                            // Reload summary
                            const summaryRes = await fetch(`${API}/admin/protected-features/summary`, { headers: authHeaders });
                            setFeaturesSummary(await summaryRes.json());
                          } catch (error) {
                            showNotification('Error scanning features', 'error');
                          }
                          setIsScanning(false);
                        }}
                        disabled={isScanning}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                          isScanning ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                        } text-white`}
                      >
                        {isScanning ? '⏳ Scanning...' : '🔍 Scan All Features (50+)'}
                      </button>

                      <button
                        onClick={async () => {
                          setIsCheckingChanges(true);
                          try {
                            const response = await fetch(`${API}/admin/protected-features/check-changes`, {
                              method: 'POST',
                              headers: authHeaders
                            });
                            const data = await response.json();
                            if (data.changes.length > 0) {
                              showNotification(`⚠️ ${data.changes.length} features have changed!`, 'warning');
                            } else {
                              showNotification('✅ No file changes detected', 'success');
                            }
                            // Reload features
                            const featuresRes = await fetch(`${API}/admin/protected-features`, { headers: authHeaders });
                            const features = await featuresRes.json();
                            setProtectedFeatures((features || []).filter(f => f && f.id));
                            // Reload summary
                            const summaryRes = await fetch(`${API}/admin/protected-features/summary`, { headers: authHeaders });
                            setFeaturesSummary(await summaryRes.json());
                          } catch (error) {
                            showNotification('Error checking changes', 'error');
                          }
                          setIsCheckingChanges(false);
                        }}
                        disabled={isCheckingChanges}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                          isCheckingChanges ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-600 hover:bg-yellow-700'
                        } text-white`}
                      >
                        {isCheckingChanges ? '⏳ Checking...' : '🔄 Check File Changes'}
                      </button>
                    </div>
                  </div>

                  {/* Filter Tabs */}
                  <div className="flex flex-wrap gap-2">
                    {['all', 'working', 'needs_testing', 'broken'].map(filter => (
                      <button
                        key={filter}
                        onClick={() => setFeaturesFilter(filter)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          featuresFilter === filter
                            ? filter === 'working' ? 'bg-green-600 text-white'
                              : filter === 'needs_testing' ? 'bg-yellow-600 text-white'
                              : filter === 'broken' ? 'bg-red-600 text-white'
                              : 'bg-blue-600 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {filter === 'all' ? '📋 All' : filter === 'working' ? '✅ Working' : filter === 'needs_testing' ? '⚠️ Needs Testing' : '❌ Broken'}
                        {' '}({filter === 'all' ? protectedFeatures.length : protectedFeatures.filter(f => f.status === filter).length})
                      </button>
                    ))}
                  </div>

                  {/* Add New Protected Feature Form - Collapsible */}
                  <details className={`rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <summary className={`p-4 cursor-pointer font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      ➕ Add Custom Protected Feature
                    </summary>
                    <div className="p-4 pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Feature Name *
                          </label>
                          <input
                            type="text"
                            value={newProtectedFeature.name}
                            onChange={(e) => setNewProtectedFeature({...newProtectedFeature, name: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="e.g., User Login System"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Location (File/Component) *
                          </label>
                          <input
                            type="text"
                            value={newProtectedFeature.location}
                            onChange={(e) => setNewProtectedFeature({...newProtectedFeature, location: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="e.g., /components/auth/UserLogin.js"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <input
                            type="text"
                            value={newProtectedFeature.description}
                            onChange={(e) => setNewProtectedFeature({...newProtectedFeature, description: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Brief description of what this feature does"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            ⚠️ Notes for AI (What NOT to do)
                          </label>
                          <textarea
                            value={newProtectedFeature.notes_for_ai}
                            onChange={(e) => setNewProtectedFeature({...newProtectedFeature, notes_for_ai: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            rows={3}
                            placeholder="e.g., DO NOT modify login validation logic. DO NOT change password hashing."
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Status
                          </label>
                          <select
                            value={newProtectedFeature.status}
                            onChange={(e) => setNewProtectedFeature({...newProtectedFeature, status: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                          >
                            <option value="working">✅ Working</option>
                            <option value="needs_testing">🔍 Needs Testing</option>
                            <option value="broken">❌ Broken</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={async () => {
                              if (!newProtectedFeature.name || !newProtectedFeature.location) {
                                showNotification('Please fill in name and location', 'error');
                                return;
                              }
                              try {
                                const response = await fetch(`${API}/admin/protected-features`, {
                                  method: 'POST',
                                  headers: { ...authHeaders, 'Content-Type': 'application/json' },
                                  body: JSON.stringify(newProtectedFeature)
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setProtectedFeatures([...protectedFeatures, data.feature]);
                                  setNewProtectedFeature({ name: '', location: '', description: '', notes_for_ai: '', status: 'working' });
                                  showNotification('Protected feature added!', 'success');
                                }
                              } catch (error) {
                                showNotification('Error adding feature', 'error');
                              }
                            }}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                          >
                            ➕ Add Feature
                          </button>
                        </div>
                      </div>
                    </div>
                  </details>

                  {/* Protected Features List */}
                  <div className={`rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
                      <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        🔒 Protected Features ({protectedFeatures.filter(f => featuresFilter === 'all' || f.status === featuresFilter).length})
                      </h4>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                      {protectedFeatures.filter(f => featuresFilter === 'all' || f.status === featuresFilter).length === 0 ? (
                        <div className="p-8 text-center">
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                            {protectedFeatures.length === 0
                              ? 'Click "Scan All Features" to auto-populate 50+ features!'
                              : 'No features match the current filter.'}
                          </p>
                        </div>
                      ) : (
                        protectedFeatures.filter(f => featuresFilter === 'all' || f.status === featuresFilter).map((feature, idx) => (
                          <div key={feature.id || `feature-${idx}`} className={`p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    {feature.name}
                                  </span>
                                  {feature.category && (
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                      {feature.category}
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    feature.status === 'working'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                      : feature.status === 'needs_testing'
                                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                  }`}>
                                    {feature.status === 'working' ? '✅ Working' : feature.status === 'needs_testing' ? '⚠️ Needs Testing' : '❌ Broken'}
                                  </span>
                                </div>
                                <p className={`text-sm mb-1 font-mono ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                  📁 {feature.location}
                                </p>
                                {feature.description && (
                                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {feature.description}
                                  </p>
                                )}
                                {feature.notes_for_ai && (
                                  <div className={`p-2 rounded-lg text-sm mb-2 ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
                                    <strong>⚠️ AI Instructions:</strong> {feature.notes_for_ai}
                                  </div>
                                )}
                                {/* File Info Row */}
                                <div className="flex flex-wrap gap-4 text-xs mt-2">
                                  {feature.file_modified_at && (
                                    <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                                      📅 Modified: {new Date(feature.file_modified_at).toLocaleString()}
                                    </span>
                                  )}
                                  {feature.last_tested_at && (
                                    <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                                      ✅ Tested: {new Date(feature.last_tested_at).toLocaleString()}
                                    </span>
                                  )}
                                  {feature.file_modified_at && feature.last_tested_at && new Date(feature.file_modified_at) > new Date(feature.last_tested_at) && (
                                    <span className="text-yellow-500 font-semibold">
                                      ⚠️ File changed after last test!
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 flex-shrink-0">
                                <button
                                  onClick={async () => {
                                    console.log('[VIEW DETAILS] Feature object:', feature);
                                    console.log('[VIEW DETAILS] feature.id =', feature.id, '| type:', typeof feature.id);
                                    if (!feature.id) {
                                      showNotification('Feature has no ID - cannot load details. Check console for feature object.', 'error');
                                      return;
                                    }
                                    try {
                                      console.log('[VIEW DETAILS] Fetching info for:', feature.id);
                                      const response = await fetch(`${API}/admin/protected-features/${feature.id}/file-info`, {
                                        headers: authHeaders
                                      });
                                      console.log('[VIEW DETAILS] Response status:', response.status);
                                      if (!response.ok) {
                                        const errText = await response.text();
                                        console.error('[VIEW DETAILS] Error response:', errText);
                                        showNotification(`Error: ${response.status} - ${errText}`, 'error');
                                        return;
                                      }
                                      const data = await response.json();
                                      console.log('[VIEW DETAILS] Data:', data);
                                      if (data && data.feature) {
                                        setSelectedFeatureInfo(data);
                                      } else {
                                        console.error('[VIEW DETAILS] No feature in data:', data);
                                        showNotification('No feature data returned - check console', 'error');
                                      }
                                    } catch (error) {
                                      console.error('[VIEW DETAILS] Error:', error);
                                      showNotification('Error loading file info: ' + error.message, 'error');
                                    }
                                  }}
                                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded"
                                >
                                  📊 View Details
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!feature.id) { showNotification('Feature has no ID', 'error'); return; }
                                    try {
                                      await fetch(`${API}/admin/protected-features/${feature.id}/test`, {
                                        method: 'PUT',
                                        headers: authHeaders
                                      });
                                      const updated = protectedFeatures.map(f =>
                                        f.id === feature.id ? {...f, last_tested_at: new Date().toISOString(), status: 'working'} : f
                                      );
                                      setProtectedFeatures(updated);
                                      showNotification('Marked as tested!', 'success');
                                    } catch (error) {
                                      showNotification('Error updating', 'error');
                                    }
                                  }}
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded"
                                >
                                  ✅ Mark Tested
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!feature.id) { showNotification('Feature has no ID', 'error'); return; }
                                    try {
                                      await fetch(`${API}/admin/protected-features/${feature.id}/broken`, {
                                        method: 'PUT',
                                        headers: authHeaders
                                      });
                                      const updated = protectedFeatures.map(f =>
                                        f.id === feature.id ? {...f, status: 'broken'} : f
                                      );
                                      setProtectedFeatures(updated);
                                      showNotification('Marked as broken', 'warning');
                                    } catch (error) {
                                      showNotification('Error updating', 'error');
                                    }
                                  }}
                                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                                >
                                  ❌ Mark Broken
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!feature.id) { showNotification('Feature has no ID', 'error'); return; }
                                    try {
                                      const response = await fetch(`${API}/admin/protected-features/${feature.id}/snapshot`, {
                                        method: 'POST',
                                        headers: authHeaders
                                      });
                                      const data = await response.json();
                                      showNotification(`Created ${data.snapshots?.length || 0} snapshot(s)`, 'success');
                                    } catch (error) {
                                      showNotification('Error creating snapshot', 'error');
                                    }
                                  }}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
                                >
                                  📸 Snapshot
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Feature Details Modal */}
                  {selectedFeatureInfo && selectedFeatureInfo.feature && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className={`p-4 border-b sticky top-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} flex justify-between items-center`}>
                          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            📊 {selectedFeatureInfo.feature?.name || 'Feature'} - Details
                          </h3>
                          <button
                            onClick={() => setSelectedFeatureInfo(null)}
                            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="p-6 space-y-6">
                          {/* File Information */}
                          <div>
                            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📁 Files</h4>
                            {selectedFeatureInfo.files.map((file, idx) => (
                              <div key={idx} className={`p-3 rounded-lg mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                <p className={`font-mono text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{file.path}</p>
                                <div className="flex flex-wrap gap-4 mt-2 text-sm">
                                  <span className={file.exists ? 'text-green-500' : 'text-red-500'}>
                                    {file.exists ? '✅ Exists' : '❌ Not Found'}
                                  </span>
                                  {file.modified_at && (
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                      Modified: {new Date(file.modified_at).toLocaleString()}
                                    </span>
                                  )}
                                  {file.size && (
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                      Size: {(file.size / 1024).toFixed(1)} KB
                                    </span>
                                  )}
                                </div>
                                {/* Git Log */}
                                {file.git_log && file.git_log.length > 0 && (
                                  <div className="mt-3">
                                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recent Commits:</p>
                                    <div className="space-y-1">
                                      {file.git_log.map((commit, cIdx) => (
                                        <div key={cIdx} className={`text-xs font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                          <span className="text-yellow-500">{commit.hash}</span> - {commit.message.substring(0, 50)}...
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Git Diff */}
                                {file.git_diff && (
                                  <div className="mt-3">
                                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>⚠️ Uncommitted Changes:</p>
                                    <pre className={`text-xs overflow-x-auto p-2 rounded ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-200 text-gray-800'}`}>
                                      {file.git_diff.substring(0, 500)}{file.git_diff.length > 500 ? '...' : ''}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Snapshots */}
                          <div>
                            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📸 Snapshots (for Restore)</h4>
                            {selectedFeatureInfo.snapshots && selectedFeatureInfo.snapshots.length > 0 ? (
                              <div className="space-y-2">
                                {selectedFeatureInfo.snapshots.map((snapshot, idx) => (
                                  <div key={idx} className={`p-3 rounded-lg flex justify-between items-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                    <div>
                                      <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{snapshot.original_file}</p>
                                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Created: {new Date(snapshot.created_at).toLocaleString()} | Size: {(snapshot.size / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Restore ${snapshot.original_file} from this snapshot? This will overwrite the current file.`)) {
                                          try {
                                            const formData = new FormData();
                                            formData.append('snapshot_id', snapshot.id);
                                            formData.append('target_file', selectedFeatureInfo.files[0]?.path || selectedFeatureInfo.feature.location);

                                            const response = await fetch(`${API}/admin/protected-features/${selectedFeatureInfo.feature.id}/restore`, {
                                              method: 'POST',
                                              headers: { 'Authorization': authHeaders['Authorization'] },
                                              body: formData
                                            });
                                            if (response.ok) {
                                              showNotification('File restored successfully!', 'success');
                                            } else {
                                              showNotification('Failed to restore', 'error');
                                            }
                                          } catch (error) {
                                            showNotification('Error restoring file', 'error');
                                          }
                                        }
                                      }}
                                      className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded"
                                    >
                                      🔄 Restore
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No snapshots yet. Click "Snapshot" on a feature to create one.
                              </p>
                            )}
                          </div>

                          {/* Close Button */}
                          <div className="flex justify-end">
                            <button
                              onClick={() => setSelectedFeatureInfo(null)}
                              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Instructions for AI */}
                  <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'border-yellow-600 bg-yellow-900/20' : 'border-yellow-400 bg-yellow-50'}`}>
                    <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      📋 How This Works
                    </h4>
                    <ul className={`list-disc list-inside text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li>Add features that are WORKING and should NOT be modified</li>
                      <li>Include the exact file location so AI knows where to avoid</li>
                      <li>Write clear "Notes for AI" explaining what NOT to change</li>
                      <li>Mark features as "Tested" after verifying they work</li>
                      <li>Mark features as "Broken" if they stop working after AI changes</li>
                      <li>AI agents should check this list before making any code changes</li>
                    </ul>
                  </div>
                </div>
                  )}
                </div>
  );
};

export default SystemManagerAdmin;
